import React, { useCallback, useEffect } from 'react'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import {
  text,
  button,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'

import headerImg from '@groovehq/internal-design-system/lib/assets/images/envelope.png'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_CHANNELS_FORWRADING_TEST } from 'ducks/drawers/types'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { useChannel } from 'ducks/channels/hooks'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { styles } from './styles'
import { drawerConfig } from '../Channels.data'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const ForwardDetails = ({
  drawerResourceId: channelId,
  drawerChannelType: channelType,
  drawerVerificationFailed: verificationFailed = 0,
  onClose,
}) => {
  const { channel, isLoading, isMissing } = useChannel(channelId)
  useEffect(
    () => {
      if (isMissing) onClose()
    },
    [isMissing, onClose]
  )

  const {
    forward_email_address: forwardEmailAddress,
    name: channelName,
    email: channelEmail,
  } =
    channel || {}
  const channelTitle = channelEmail || channelName

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_FORWRADING_TEST,
    closeIgnoresStack: false,
  })

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const handleOpenNextDrawer = useCallback(
    () => {
      openNextDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            nextDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
    },
    [openNextDrawer, channelId, nextDrawerId, channelType]
  )

  const handleContactSupport = useCallback(
    e => {
      e.preventDefault()
      onClose()
      if (window.groove && window.groove.widget) {
        window.groove.widget.open()
      }
    },
    [onClose]
  )

  const footer = (
    <ModalBtns
      saveBtnText="I've set up the forwarding"
      onSave={handleOpenNextDrawer}
      tertiaryBtnText="Back"
      onClickTertiaryBtn={handleBack}
    />
  )

  const title = verificationFailed
    ? 'Oh no, that doesn’t seem right!'
    : 'Auto-forward new emails to Groove'

  const intro = verificationFailed ? (
    <>
      It seems that the email forwarding hasn’t been setup correctly.
      <br />
      <br />
      Please check the forwarding address and try again.
      <br />
      <br />
      {`If you still need help, please `}
      <a
        onClick={handleContactSupport}
        className="grui underline"
        css={button.styles.link}
      >
        get in touch with our support team
      </a>
      {` that will give you a hand.`}
    </>
  ) : (
    `To start receiving emails from your ${channelTitle} mailbox in Groove, you will need to forward your incoming emails across from your current email tool.`
  )
  return (
    <>
      <Drawer
        title={DEFAULT_DRAWER_TITLE}
        footer={footer}
        onClose={onClose}
        isLoading={isLoading}
      >
        <HeaderWithImage
          className="grui mt-10 mb-12"
          imgWidth="120px"
          css={styles.header}
          img={headerImg}
          alt="Email forwarding"
          title={title}
          intro={intro}
        />
        <Field
          disabled
          defaultValue={forwardEmailAddress}
          css={styles.fullWidth}
          label="Forward your email to"
          showCopy
          className="grui mb-16"
        />
        {!verificationFailed && (
          <div>
            <p
              css={[paragraph.styles.preflight, text.styles.fontMedium]}
              className="grui mb-5"
            >
              Need a hand?
            </p>
            <p
              css={[
                paragraph.styles.preflight,
                text.styles.textSm,
                text.styles.textDark,
              ]}
            >
              {`Have a look at our `}
              <a
                css={button.styles.link}
                href="https://help.groovehq.com/help/getting-your-emails-into-groove-08b4e653"
                target="_blank"
                rel="noopener noreferrer"
              >
                step by step tutorials!
              </a>
            </p>
            <MessageCard type="warning" className="grui mt-11">
              <div css={text.styles.fontMedium}>Important note!</div>
              <p css={paragraph.styles.preflight} className="grui my-5">
                Certain email providers will email you a confirmation code to
                complete the email forwarding. If this applies to your provider,
                email forwarding to your Groove mailbox will only be complete
                once this verification is confirmed.
              </p>
              <p>
                <a
                  css={button.styles.link}
                  href="https://help.groovehq.com/help/forwarding-verification"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </p>
            </MessageCard>
          </div>
        )}
      </Drawer>
    </>
  )
}

export default ForwardDetails
